import {
	DataGridPro,
	DataGridProProps,
	GridActionsCellItem,
	GridFooter,
	GridFooterContainer,
	GridRowParams
} from '@mui/x-data-grid-pro';
import { getActionsColumn } from '@utils/datagrid/actions';
import { COMMON_FIELDS, IS_TEST_MODE } from 'constants/global';
import React from 'react';
import { IRowAction } from 'types/common';
import DataGridToolbar from './DataGridToolbar';
import NoDataOverlay from './Overlays/NoDataOverlay';
import NoResultsOverlay from './Overlays/NoResultsOverlay';

interface IDataGridProps extends Partial<DataGridProProps> {
	actions?: React.ReactNode;
	leftActions?: React.ReactNode;
	footer?: React.ReactNode;
	noDataMessageId: string;
	getRowActions?: (row: GridRowParams) => IRowAction[];
}

const DataGrid: React.FC<IDataGridProps> = ({
	columns,
	rows,
	columnVisibilityModel,
	actions,
	leftActions,
	footer,
	getRowActions,
	noDataMessageId,
	sx,
	...rest
}) => {
	const [selectedRow, setSelectedRow] = React.useState<GridRowParams>();
	const [selectedRowAction, setSelectedRowAction] = React.useState<
		IRowAction | undefined
	>();

	const [visibilityModel, setVisibilityModel] = React.useState(
		columnVisibilityModel
	);

	const handleActionClicked = React.useCallback(
		(row: GridRowParams, action: IRowAction) => () => {
			setSelectedRow(row);
			setSelectedRowAction(action);
		},
		[]
	);

	const handleActionDialogReset = React.useCallback(() => {
		setSelectedRow(undefined);
		setSelectedRowAction(undefined);
	}, []);

	// Otherwise filter will be applied on hidden fields
	const filteredCols = React.useMemo(() => {
		const cols = columns!.filter(
			(column) =>
				column.field === COMMON_FIELDS.id ||
				!Object.keys(columnVisibilityModel!).includes(column.field)
		);

		if (getRowActions) {
			const getActions = (row: GridRowParams) => {
				const rowActions = getRowActions(row);
				const rowActionCells: any[] = [];
				rowActions?.forEach((action) => {
					rowActionCells.push(
						<GridActionsCellItem
							key={action.key}
							label={action.label}
							onClick={handleActionClicked(row, action)}
							showInMenu
						/>
					);
				});

				return rowActionCells;
			};
			cols.push(getActionsColumn(getActions));
		}

		return cols;
	}, [columnVisibilityModel, columns, getRowActions, handleActionClicked]);

	const toolbar = React.useCallback(
		() => <DataGridToolbar actions={actions} leftActions={leftActions} />,
		[actions, leftActions]
	);

	const gridFooter = React.useCallback(
		() =>
			footer ? (
				<GridFooterContainer>
					{footer}
					<GridFooter />
				</GridFooterContainer>
			) : (
				<GridFooter />
			),
		[footer]
	);
	const noResultsOverlay = React.useCallback(() => <NoResultsOverlay />, []);
	const noDataOverlay = React.useCallback(
		() => <NoDataOverlay messageId={noDataMessageId} />,
		[noDataMessageId]
	);

	const ActionDialogComp = selectedRowAction?.Dialog!;
	return (
		<>
			{selectedRow && (
				<ActionDialogComp
					row={selectedRow}
					onComplete={handleActionDialogReset}
				/>
			)}
			<DataGridPro
				sx={{
					...sx,
					boxShadow: 0,
					border: 0,
					height: '100%',
					minHeight: '75px',
					overflow: 'scroll'
				}}
				sortingOrder={['asc', 'desc']}
				columns={filteredCols}
				rows={rows!}
				autoHeight={false}
				checkboxSelection
				disableVirtualization={IS_TEST_MODE}
				columnVisibilityModel={visibilityModel}
				onColumnVisibilityModelChange={setVisibilityModel}
				disableColumnMenu
				components={{
					Toolbar: toolbar,
					NoResultsOverlay: noResultsOverlay,
					NoRowsOverlay: noDataOverlay,
					Footer: gridFooter
				}}
				{...rest}
			/>
		</>
	);
};

DataGrid.defaultProps = {
	actions: null,
	leftActions: null,
	footer: null,
	getRowActions: undefined
};

export default DataGrid;
